import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import styles from './CertificateView.module.css';
import DataFallback from '../components/feedback/DataFallback';

function CertificateView() {
  const { certificateId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const certificateRef = useRef();
  const [certificate, setCertificate] = useState(location.state?.certificate || null);
  const [loading, setLoading] = useState(!certificate);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!certificate && certificateId) {
      fetchCertificate();
    }
  }, [certificateId, certificate]);

  const fetchCertificate = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/certification/certificate/${certificateId}/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch certificate');
      }

      const data = await response.json();
      setCertificate(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const downloadPDF = async () => {
    if (!certificateRef.current) return;

    try {
      // Hide the toolbar during PDF generation
      const toolbar = document.querySelector(`.${styles.toolbar}`);
      if (toolbar) toolbar.style.display = 'none';

      const canvas = await html2canvas(certificateRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('l', 'mm', 'a4'); // landscape orientation
      
      const imgWidth = 297; // A4 width in mm (landscape)
      const pageHeight = 210; // A4 height in mm (landscape)
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;

      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`3SC_Certificate_${certificate.grade}_${certificate.certificate_code}.pdf`);

      // Show the toolbar again
      if (toolbar) toolbar.style.display = 'flex';
    } catch (error) {
      console.error('PDF generation failed:', error);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>Loading certificate...</div>
      </div>
    );
  }

  if (error || !certificate) {
    // "Certificate Not Found" was shown for both a missing certificate and a
    // failed request, so a brief outage told someone their certificate did not
    // exist. Only the genuine absence says that now.
    return (
      <div className={styles.page}>
        <DataFallback
          variant="pane"
          error={error || { kind: 'notFound', retryable: false }}
          onRetry={error ? () => window.location.reload() : undefined}
        >
          <button onClick={() => navigate('/certification')} className={styles.backBtn}>
            Back to Certification Dashboard
          </button>
        </DataFallback>
      </div>
    );
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getGradeColor = (grade) => {
    if (grade.startsWith('A')) return '#28a745';
    if (grade.startsWith('B')) return '#17a2b8';
    if (grade.startsWith('C')) return '#6f42c1';
    if (grade.startsWith('D')) return '#6c757d';
    if (grade.startsWith('E')) return '#dc3545';
    return '#6c757d';
  };

  const getGradeDescription = (grade) => {
    const descriptions = {
      'A+': 'Outstanding', 'A': 'Excellent', 'A-': 'Very Good Plus',
      'B+': 'Very Good', 'B': 'Good', 'B-': 'Good Minus',
      'C+': 'Satisfactory Plus', 'C': 'Satisfactory', 'C-': 'Satisfactory Minus',
      'D+': 'Needs Improvement Plus', 'D': 'Needs Improvement', 'D-': 'Needs Improvement Minus',
      'E+': 'Unsatisfactory Plus', 'E': 'Unsatisfactory', 'E-': 'Poor'
    };
    return descriptions[grade] || 'Ungraded';
  };

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <button onClick={() => navigate('/certification')} className={styles.backBtn}>
          ← Back to Dashboard
        </button>
        <div className={styles.toolbarRight}>
          <button onClick={() => window.print()} className={styles.printBtn}>
            🖨️ Print
          </button>
          <button onClick={downloadPDF} className={styles.downloadBtn}>
            📄 Download PDF
          </button>
        </div>
      </div>

      <div ref={certificateRef} className={styles.certificate}>
        <div className={styles.watermark}>CERTIFIED</div>
        
        <div className={styles.header}>
          <div className={styles.logo}>
            <div className={styles.logoCircle}>
              <span className={styles.logoText}>3SC</span>
            </div>
          </div>
          <h1 className={styles.title}>SUSTAINABILITY CERTIFICATION</h1>
          <p className={styles.subtitle}>Suspire Sustainability Certification System</p>
          <div className={styles.period}>Certificate Period: {certificate.period}</div>
        </div>

        <div className={styles.content}>
          <div className={styles.brandSection}>
            <h2 className={styles.brandName}>
              {certificate.brand_name || certificate.user?.company_name || 'Brand Name'}
            </h2>
            <p className={styles.certificationText}>
              has successfully completed the 3SC Sustainability Assessment
              and achieved the following certification level:
            </p>
          </div>

          <div className={styles.gradeSection}>
            <div 
              className={styles.gradeBadge}
              style={{ backgroundColor: getGradeColor(certificate.grade) }}
            >
              <span className={styles.gradeText}>{certificate.grade}</span>
            </div>
            <div className={styles.gradeDescription}>
              {getGradeDescription(certificate.grade)}
            </div>
            <div className={styles.scoreInfo}>
              <div className={styles.scoreMain}>
                {certificate.percentage_score}%
              </div>
              <div className={styles.scoreDetail}>
                ({certificate.total_score} out of {certificate.max_possible_score} points)
              </div>
            </div>
          </div>

          <div className={styles.details}>
            <div className={styles.detailGrid}>
              <div className={styles.detailItem}>
                <span className={styles.label}>Certificate Code:</span>
                <span className={styles.value}>{certificate.certificate_code}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.label}>Issue Date:</span>
                <span className={styles.value}>{formatDate(certificate.submitted_at)}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.label}>Expiry Date:</span>
                <span className={styles.value}>{formatDate(certificate.expires_at)}</span>
              </div>
              <div className={styles.detailItem}>
                <span className={styles.label}>Valid For:</span>
                <span className={styles.value}>1 Year</span>
              </div>
            </div>
          </div>

          {certificate.certificate_data?.visual_chart_data && (
            <div className={styles.performanceChart}>
              <h3>Sustainability Performance Scale</h3>
              <div className={styles.gradeScale}>
                {certificate.certificate_data.visual_chart_data.grades.map((gradeInfo) => (
                  <div 
                    key={gradeInfo.grade}
                    className={`${styles.gradePoint} ${
                      certificate.grade === gradeInfo.grade ? styles.achieved : ''
                    }`}
                  >
                    <div 
                      className={styles.gradeLabel}
                      style={{ 
                        backgroundColor: getGradeColor(gradeInfo.grade),
                        color: 'white'
                      }}
                    >
                      {gradeInfo.grade}
                    </div>
                    <div className={styles.gradeRange}>
                      {gradeInfo.min_percentage}%-{gradeInfo.max_percentage}%
                    </div>
                    <div className={styles.gradeDesc}>{gradeInfo.description}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className={styles.footer}>
          <div className={styles.verification}>
            <div className={styles.qrCode}>
              <div className={styles.qrPlaceholder}>QR</div>
            </div>
            <div className={styles.verificationText}>
              <p><strong>Verify this certificate online at:</strong></p>
              <p>suspire.com/verify/{certificate.certificate_code}</p>
              <p className={styles.small}>Scan QR code or visit website to verify authenticity</p>
            </div>
          </div>
          
          <div className={styles.signatureSection}>
            <div className={styles.signature}>
              <div className={styles.signatureLine}></div>
              <p className={styles.signerName}>Dr. Sarah Johnson</p>
              <p className={styles.signerTitle}>Chief Sustainability Officer</p>
              <p className={styles.signerOrg}>Suspire Certification Authority</p>
            </div>
            <div className={styles.seal}>
              <div className={styles.sealCircle}>
                <div className={styles.sealText}>
                  <div>OFFICIAL</div>
                  <div>SEAL</div>
                  <div>2025</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.certificateFooter}>
          <p>This certificate demonstrates commitment to sustainable business practices and environmental responsibility.</p>
          <p><strong>ISO 14001:2015 Compliant • B Corp Aligned • UN SDG Integrated</strong></p>
        </div>
      </div>
    </div>
  );
}

export default CertificateView;
